import type { Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import type { EditorView } from '@codemirror/view';
import { allTags, titles } from '../data/links.ts';

/**
 * Zwei Vervollständigungen, mehr braucht der Editor nicht: `[[` schlägt Notiztitel
 * vor, `/` am Zeilenanfang öffnet die kurze Einfügen-Liste. Beides ist bewusst knapp
 * gehalten — es ist das gesamte Einfügen-Menü der App.
 */

export function wikilinkSource(context: CompletionContext): CompletionResult | null {
  const match = context.matchBefore(/\[\[[^\]\n]*/);
  if (!match) return null;
  const typed = match.text.slice(2).toLowerCase();
  const options = titles()
    .filter((title) => title.toLowerCase().includes(typed))
    .slice(0, 20)
    .map((title) => ({ label: title, type: 'text', apply: `[[${title}]]` }));
  if (options.length === 0) return null;
  // `from` zeigt auf die öffnenden Klammern, damit das Einsetzen sie mit ersetzt.
  // CodeMirror würde die Vorschläge dann gegen „[[Met" prüfen und alle verwerfen —
  // deshalb filtern wir selbst und schalten die Nachfilterung ab.
  return { from: match.from, options, filter: false };
}

export function tagSource(context: CompletionContext): CompletionResult | null {
  const match = context.matchBefore(/(^|\s)#[\p{L}\p{N}_/-]*/u);
  if (!match) return null;
  const offset = match.text.startsWith('#') ? 0 : 1;
  const typed = match.text.slice(offset + 1).toLowerCase();
  const options = allTags()
    .filter((tag) => tag.includes(typed))
    .slice(0, 15)
    .map((tag) => ({ label: `#${tag}`, type: 'keyword', apply: `#${tag}` }));
  if (options.length === 0) return null;
  return { from: match.from + offset, options };
}

const SNIPPETS: { label: string; detail: string; insert: string; cursor?: number }[] = [
  { label: 'Überschrift', detail: 'Abschnitt', insert: '## ' },
  { label: 'Aufgabe', detail: 'Checkbox', insert: '- [ ] ' },
  { label: 'Tabelle', detail: 'Markdown', insert: '| Spalte | Spalte |\n| --- | --- |\n|  |  |\n' },
  {
    label: 'Datenbank',
    detail: 'Tabelle über Notizen',
    insert:
      '```athene-db\nfrom: all\ncolumns:\n  - title\n  - status: select [offen, laufend, erledigt]\n  - deadline: date\nsort: deadline asc\n```\n',
  },
  { label: 'Codeblock', detail: 'mit Sprache', insert: '```\n\n```\n', cursor: 4 },
  { label: 'Secret', detail: 'verdeckte Zugangsdaten', insert: '```secret\n\n```\n', cursor: 10 },
  { label: 'Datum', detail: 'heute', insert: new Date().toISOString().slice(0, 10) },
  { label: 'Trennlinie', detail: '', insert: '\n---\n\n' },
];

export function slashSource(context: CompletionContext): CompletionResult | null {
  const match = context.matchBefore(/^\s*\/\w*/);
  if (!match) return null;
  const typed = match.text.trim().slice(1).toLowerCase();
  const options = SNIPPETS.filter((snippet) => snippet.label.toLowerCase().includes(typed)).map(
    (snippet) => ({
      label: snippet.label,
      detail: snippet.detail,
      type: 'keyword',
      apply: (view: EditorView, _completion: Completion, from: number, to: number) => {
        view.dispatch({
          changes: { from, to, insert: snippet.insert },
          selection: { anchor: from + (snippet.cursor ?? snippet.insert.length) },
        });
      },
    }),
  );
  if (options.length === 0) return null;
  return {
    from: match.from + (match.text.length - match.text.trimStart().length),
    options,
    filter: false,
  };
}
